import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Activity, Maximize2 } from 'lucide-react';
import { format, startOfDay, differenceInMinutes } from 'date-fns';
import { useTranslation } from 'react-i18next';
import type { Trip, Device, Position } from '@/lib/traccar-api';
import { useMemo, useState, memo, useEffect, useRef } from 'react';

interface ActivityChartProps {
  trips: Trip[];
  devices: Device[];
  positions?: Position[];
  onDeviceSelect?: (deviceId: number | null) => void;
  selectedDeviceId?: number | null;
}

type TimelineSegment = {
  type: 'trip' | 'stop';
  startPercent: number;
  widthPercent: number;
  startTime: string;
  endTime: string;
  duration: number;
  distance?: number;
  address?: string;
};

interface DeviceTimeline {
  device: Device;
  segments: TimelineSegment[];
  tripCount: number;
  totalKm: number;
}

export const ActivityChart = memo(({ trips, devices, positions = [], onDeviceSelect, selectedDeviceId }: ActivityChartProps) => {
  const positionMap = useMemo(() => new Map(positions.map(p => [p.deviceId, p])), [positions]);
  const { t } = useTranslation();
  const dateKey = new Date().toDateString();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => startOfDay(new Date()), [dateKey]);
  const minutesInDay = 24 * 60;

  const deviceTimelines = useMemo<DeviceTimeline[]>(() => {
    const tripsByDevice = new Map<number, Trip[]>();
    for (const trip of trips) {
      const list = tripsByDevice.get(trip.deviceId);
      if (list) list.push(trip);
      else tripsByDevice.set(trip.deviceId, [trip]);
    }

    return devices.map(device => {
      const deviceTrips = tripsByDevice.get(device.id) ?? [];

      const segments: TimelineSegment[] = [];

      // Add trip segments
      deviceTrips.forEach(trip => {
        const start = new Date(trip.startTime);
        const end = new Date(trip.endTime);
        const startMinutes = differenceInMinutes(start, today);
        const endMinutes = differenceInMinutes(end, today);

        if (startMinutes < minutesInDay && endMinutes > 0) {
          const clampedStart = Math.max(0, startMinutes);
          const clampedEnd = Math.min(minutesInDay, endMinutes);

          segments.push({
            type: 'trip',
            startPercent: (clampedStart / minutesInDay) * 100,
            widthPercent: ((clampedEnd - clampedStart) / minutesInDay) * 100,
            startTime: trip.startTime,
            endTime: trip.endTime,
            duration: trip.duration,
            distance: trip.distance,
          });
        }
      });

      const totalKm = deviceTrips.reduce((sum, t) => sum + (t.distance || 0), 0) / 1000;

      return {
        device,
        segments: segments.sort((a, b) => a.startPercent - b.startPercent),
        tripCount: deviceTrips.length,
        totalKm,
      };
    }).sort((a, b) => a.device.name.localeCompare(b.device.name));
  }, [devices, trips, today, minutesInDay]);

  const totalTrips = trips.length;
  const totalKm = trips.reduce((sum, t) => sum + (t.distance || 0), 0) / 1000;
  const [isMaximized, setIsMaximized] = useState(false);
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (selectedDeviceId != null) {
      rowRefs.current.get(selectedDeviceId)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedDeviceId]);

  const TimelineContent = ({ expanded = false }: { expanded?: boolean }) => (
    <>
      {deviceTimelines.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 text-muted-foreground">
          <Activity className="h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">{t('common.noData')}</p>
        </div>
      ) : (
        <div style={{ minHeight: 'auto' }}>
          {/* Time axis header */}
          <div className="flex items-center mb-2 sticky top-0 bg-card z-10">
            <div className={`${expanded ? 'w-32' : 'w-24'} flex-shrink-0`} />
            <div className="flex-1 flex justify-between text-[10px] text-muted-foreground">
              {[0, 6, 12, 18, 24].map(hour => (
                <span key={hour}>{hour.toString().padStart(2, '0')}:00</span>
              ))}
            </div>
          </div>

          {/* Device rows */}
          <div className="space-y-1">
            {deviceTimelines.map(({ device, segments, totalKm: deviceKm }) => (
              <div
                key={device.id}
                ref={(el) => { if (el) rowRefs.current.set(device.id, el); else rowRefs.current.delete(device.id); }}
                className={`flex items-center ${expanded ? 'h-8' : 'h-7'} group cursor-pointer hover:bg-muted/50 rounded transition-all ${selectedDeviceId === device.id ? 'bg-muted' : selectedDeviceId ? 'opacity-40' : ''}`}
                onClick={() => onDeviceSelect?.(selectedDeviceId === device.id ? null : device.id)}
              >
                {/* Device name */}
                <div className={`${expanded ? 'w-32' : 'w-24'} flex-shrink-0 pr-2 flex items-center gap-1.5`}>
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={(() => {
                    const pos = positionMap.get(device.id);
                    const isOnline = device.status === 'online';
                    const isMoving = isOnline && pos && pos.speed > 0;
                    const ignition = pos?.attributes?.ignition as boolean | undefined;
                    const color = isMoving ? '#22c55e' : (isOnline && ignition) ? '#eab308' : isOnline ? '#f97316' : '#ef4444';
                    return { backgroundColor: color };
                  })()} />
                  <span className={`${expanded ? 'text-sm' : 'text-xs'} font-medium truncate`} title={device.name}>
                    {device.name}
                  </span>
                </div>

                {/* Timeline bar */}
                <div className={`flex-1 ${expanded ? 'h-6' : 'h-5'} bg-muted/30 relative`}>
                  {/* Hour grid lines */}
                  {[6, 12, 18].map(hour => (
                    <div
                      key={hour}
                      className="absolute top-0 bottom-0 w-px bg-border/50"
                      style={{ left: `${(hour / 24) * 100}%` }}
                    />
                  ))}

                  {/* Activity segments */}
                  {segments.map((segment, idx) => (
                    <div
                      key={idx}
                      className={`absolute top-0.5 bottom-0.5 cursor-pointer transition-opacity hover:opacity-80 ${
                        segment.type === 'trip' 
                          ? 'bg-success' 
                          : 'bg-destructive'
                      }`}
                      style={{
                        left: `${segment.startPercent}%`,
                        width: `${Math.max(segment.widthPercent, 0.5)}%`,
                      }}
                      title={`${segment.type === 'trip' ? 'Trip' : 'Stop'}: ${format(new Date(segment.startTime), 'HH:mm')} - ${format(new Date(segment.endTime), 'HH:mm')}${segment.distance ? ` (${(segment.distance / 1000).toFixed(1)} km)` : ''}${segment.address ? ` at ${segment.address}` : ''}`}
                    />
                  ))}

                  {/* Empty state indicator */}
                  {segments.length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[10px] text-muted-foreground">No activity</span>
                    </div>
                  )}

                  {/* Km overlay at end */}
                  <div className={`absolute right-1 top-0 bottom-0 flex items-center ${expanded ? 'text-xs' : 'text-[10px]'} text-foreground/70 font-medium pointer-events-none`}>
                    {deviceKm.toFixed(1)} km
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <>
      <Card className="h-full flex flex-col overflow-hidden">
        <CardHeader className="pb-2 flex-shrink-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('dashboard.activity')}
            <span className="ml-auto text-xs font-normal text-muted-foreground">
              {totalTrips} {t('history.trips')} · {totalKm.toFixed(1)} km
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsMaximized(true)}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 min-h-0 p-0 relative">
          <div className="absolute inset-0 overflow-auto px-6 pb-6">
            <TimelineContent />
          </div>
        </CardContent>
      </Card>

      <Dialog open={isMaximized} onOpenChange={setIsMaximized}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              {t('dashboard.activity')}
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                {totalTrips} {t('history.trips')} · {totalKm.toFixed(1)} km
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto pr-2">
            <TimelineContent expanded />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}, (prev, next) => {
  const reasons: string[] = [];
  if (prev.trips !== next.trips) reasons.push('trips');
  if (prev.selectedDeviceId !== next.selectedDeviceId) reasons.push('selectedDeviceId');
  if (prev.onDeviceSelect !== next.onDeviceSelect) reasons.push('onDeviceSelect');
  if (prev.devices.length !== next.devices.length) reasons.push(`devices.length ${prev.devices.length}->${next.devices.length}`);
  else {
    for (let i = 0; i < prev.devices.length; i++) {
      const p = prev.devices[i], n = next.devices[i];
      if (p.id !== n.id || p.name !== n.name || p.status !== n.status) {
        reasons.push(`device[${i}] ${p.id}:${p.name}/${p.status}->${n.id}:${n.name}/${n.status}`);
        break;
      }
    }
  }
  return reasons.length <= 0;

});
